import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU, maskData } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Mail, Phone, MapPin, CreditCard, Calendar, 
  Users, Building2, Lock, Shield
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import NotesBlock from '@/components/NotesBlock';
import CallLogCard from '@/components/CallLogCard';
import CallLogModal from '@/components/modals/CallLogModal';
import { toast } from 'sonner';

export default function IndividualProfile() {
  const { clientId, individualId } = useParams();
  const { client } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [individual, setIndividual] = useState(null);
  const [allIndividuals, setAllIndividuals] = useState([]);
  const [entities, setEntities] = useState([]);
  const [advisers, setAdvisers] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSensitive, setShowSensitive] = useState({});
  const [showCallLogModal, setShowCallLogModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !clientId || !individualId) return;
      try {
        const [indRes, allIndRes, entRes, advRes, callLogsRes] = await Promise.all([
          api.getIndividual(token, clientId, individualId),
          api.getIndividuals(token, clientId),
          api.getEntities(token, clientId),
          api.getAdvisers(token, clientId),
          api.getCallLogs(token, clientId)
        ]);
        setIndividual(indRes.data);
        setAllIndividuals(allIndRes.data);
        setEntities(entRes.data);
        setAdvisers(advRes.data);
        // Filter call logs for this individual
        setCallLogs(callLogsRes.data.filter(log => 
          log.linkedIndividualId === individualId || 
          log.participants?.some(p => p.id === individualId)
        ));
      } catch (error) {
        console.error('Failed to fetch individual:', error);
        navigate(`/app/clients/${clientId}/individuals`);
      }
      setLoading(false);
    };
    fetchData();
  }, [token, clientId, individualId, navigate]);

  const handleSaveNotes = async (notes) => {
    try {
      const response = await api.updateIndividualNotes(token, clientId, individualId, notes);
      setIndividual(response.data);
      toast.success('Notes saved');
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const handleAddCallLog = async (callLogData) => {
    try {
      const response = await api.createCallLog(token, clientId, {
        ...callLogData,
        linkedIndividualId: individualId
      });
      setCallLogs([response.data, ...callLogs]);
      toast.success('Call logged successfully');
    } catch (error) {
      console.error('Failed to create call log:', error);
      toast.error('Failed to log call');
    }
  };

  const toggleSensitive = (field) => {
    setShowSensitive(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Get related individuals
  const getRelatedIndividual = (relId) => {
    return allIndividuals.find(i => i.id === relId);
  };

  // Get linked entities
  const linkedEntities = entities.filter(e => e.linkedPeople?.includes(individualId));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!individual) {
    return (
      <div className="empty-state">
        <Users className="w-12 h-12" />
        <p className="text-lg font-medium text-[#5D524D]">Individual not found</p>
      </div>
    );
  }

  const SensitiveField = ({ field, value, maskType }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button 
            onClick={() => toggleSensitive(field)}
            className="masked-field cursor-pointer hover:text-[#4A3B32]"
          >
            <Lock className="w-3.5 h-3.5" />
            {showSensitive[field] ? value : maskData(value, maskType)}
          </button>
        </TooltipTrigger>
        <TooltipContent className="sensitive-tooltip">
          <p>Sensitive fields are permission-controlled and fully audited in the live product.</p>
          <p className="text-xs mt-1 opacity-75">Click to toggle visibility (demo only)</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="animate-fade-in-up" data-testid="individual-profile">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(`/app/clients/${clientId}/individuals`)}
        className="caffeine-btn-ghost mb-4"
        data-testid="back-to-individuals-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Individuals
      </Button>

      {/* Profile Header */}
      <div className="caffeine-card p-6 mb-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-2xl font-medium">
            {individual.firstName[0]}{individual.lastName[0]}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h1 className="font-heading text-2xl text-[#2C2420]">
                {individual.firstName} {individual.lastName}
              </h1>
              {individual.confidential && (
                <span className="badge-restricted">
                  <Shield className="w-3 h-3 mr-1" />
                  Confidential
                </span>
              )}
            </div>
            <p className="text-[#5D524D] mb-1">{individual.role || 'No role assigned'}</p>
            <p className="text-sm text-[#8C8078]">Generation {individual.generation} • {client?.name}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Details */}
        <div className="caffeine-card p-6">
          <h2 className="font-heading text-lg text-[#2C2420] mb-4">Personal Details</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Date of Birth</p>
                <SensitiveField field="dob" value={formatDateAU(individual.dateOfBirth)} maskType="dob" />
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Email</p>
                <p className="text-[#2C2420]">{individual.email || '—'}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Phone</p>
                <SensitiveField field="phone" value={individual.phone} maskType="phone" />
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Residential Address</p>
                <SensitiveField field="address" value={individual.address} maskType="address" />
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-[#8C8078] mt-0.5" />
              <div>
                <p className="text-xs text-[#8C8078] uppercase tracking-wider mb-1">Driver Licence</p>
                <SensitiveField field="licence" value={individual.driversLicence} maskType="licence" />
              </div>
            </div>
          </div>
        </div>

        {/* Relationships */}
        <div className="caffeine-card p-6">
          <h2 className="font-heading text-lg text-[#2C2420] mb-4">Family Relationships</h2>
          {individual.relationships && individual.relationships.length > 0 ? (
            <div className="space-y-3">
              {individual.relationships.map((rel, idx) => {
                const relatedPerson = getRelatedIndividual(rel.individualId);
                return (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-[#F9F7F5] rounded-lg cursor-pointer hover:bg-[#F0EBE6] transition-colors"
                    onClick={() => relatedPerson && navigate(`/app/clients/${clientId}/individuals/${rel.individualId}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-sm">
                        {relatedPerson ? `${relatedPerson.firstName[0]}${relatedPerson.lastName[0]}` : '?'}
                      </div>
                      <div>
                        <p className="font-medium text-[#2C2420]">
                          {relatedPerson ? `${relatedPerson.firstName} ${relatedPerson.lastName}` : 'Unknown'}
                        </p>
                        <p className="text-xs text-[#8C8078]">{rel.type}</p>
                      </div>
                    </div>
                    <Users className="w-4 h-4 text-[#8C8078]" />
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[#8C8078]">No relationships recorded</p>
          )}
        </div>

        {/* Linked Entities */}
        <div className="caffeine-card p-6 lg:col-span-2">
          <h2 className="font-heading text-lg text-[#2C2420] mb-4">Linked Entities</h2>
          {linkedEntities.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {linkedEntities.map(entity => (
                <div 
                  key={entity.id}
                  className="p-4 bg-[#F9F7F5] rounded-lg cursor-pointer hover:bg-[#F0EBE6] transition-colors"
                  onClick={() => navigate(`/app/clients/${clientId}/entities/${entity.id}`)}
                  data-testid={`linked-entity-${entity.id}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-5 h-5 text-[#4A3B32]" />
                    <span className="font-medium text-[#2C2420]">{entity.name}</span>
                  </div>
                  <p className="text-sm text-[#8C8078]">{entity.type}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#8C8078]">No linked entities</p>
          )}
        </div>

        {/* Notes */}
        <NotesBlock
          notes={individual?.notes}
          updatedAt={individual?.notesUpdatedAt}
          updatedBy={individual?.notesUpdatedBy}
          onSave={handleSaveNotes}
          placeholder={`Add notes about ${individual?.firstName} ${individual?.lastName}...`}
          className="lg:col-span-1"
        />

        {/* Call Log */}
        <CallLogCard
          callLogs={callLogs}
          onViewAll={() => navigate(`/app/clients/${clientId}/activity`)}
          onAddNew={() => setShowCallLogModal(true)}
          maxDisplay={3}
          title={`Call Log - ${individual?.firstName}`}
        />
      </div>

      {/* Call Log Modal */}
      <CallLogModal
        isOpen={showCallLogModal}
        onClose={() => setShowCallLogModal(false)}
        onSave={handleAddCallLog}
        individuals={allIndividuals}
        advisers={advisers}
      />
    </div>
  );
}
